import { useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/auth';
import { useGamificationStore } from '@/stores/gamification';
import { useOnboardingStore } from '@/stores/onboarding';
import {
  ArrowRight,
  BadgeCheck,
  Lightbulb,
  Megaphone,
  Rocket,
  Settings2,
  ShieldCheck,
  Timer,
  Users,
} from 'lucide-react';

const ONBOARDING_HERO_IMAGE =
  'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80';

function getTaskIcon(category?: string) {
  const value = (category ?? '').toLowerCase();
  if (value.includes('security') || value.includes('sicherheit')) return ShieldCheck;
  if (value.includes('team') || value.includes('user')) return Users;
  if (value.includes('setup') || value.includes('admin') || value.includes('einstellung')) return Settings2;
  if (value.includes('marketing') || value.includes('kampagne')) return Megaphone;
  if (value.includes('strategie') || value.includes('idee')) return Lightbulb;
  return Rocket;
}

export function OnboardingPage() {
  const user = useAuthStore((s) => s.user);
  const data = useOnboardingStore((s) => s.payload);
  const loading = useOnboardingStore((s) => s.loading);
  const fetchOnboarding = useOnboardingStore((s) => s.fetch);
  const resetOnboarding = useOnboardingStore((s) => s.reset);

  const completion = useMemo(() => {
    const tasks = data?.tasks ?? [];
    const completed = tasks.filter((t) => Boolean(t.progress?.completed_at)).length;
    return { completed, total: tasks.length };
  }, [data]);

  const estimatedTotalMinutes = useMemo(() => {
    return (data?.tasks ?? []).reduce((sum, task) => sum + (task.estimated_minutes ?? 0), 0);
  }, [data]);

  const remainingMinutes = useMemo(() => {
    return (data?.tasks ?? []).reduce((sum, task) => {
      const done = Boolean(task.progress?.completed_at);
      return done ? sum : sum + (task.estimated_minutes ?? 0);
    }, 0);
  }, [data]);

  useEffect(() => {
    if (!user?.id) {
      resetOnboarding();
      return;
    }
    void fetchOnboarding().catch((e) => toast.error(e instanceof Error ? e.message : 'Onboarding konnte nicht geladen werden.'));
  }, [user?.id, fetchOnboarding, resetOnboarding]);

  const toggleTask = async (taskId: string) => {
    try {
      const result = await api.patch<{ success?: boolean; error?: string }>(`/onboarding/tasks/${taskId}/toggle`);
      if (result.success === false) throw new Error(result.error || 'Aufgabe konnte nicht gespeichert werden.');
      await fetchOnboarding();
      if (user?.id) void useGamificationStore.getState().fetch(user.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Aufgabe konnte nicht gespeichert werden.');
    }
  };

  const openTaskAction = (path?: string) => {
    if (!path) return;
    window.location.hash = path.startsWith('/') ? path : `/${path}`;
  };

  if (loading && !data) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-border border-t-primary-600 dark:border-t-primary-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Onboarding</CardTitle>
          <CardDescription>Aktuell ist kein Onboarding-Template fuer dich aktiv.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-sm dark:bg-background">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.14] dark:opacity-[0.1]"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.96), rgba(255,255,255,0.75)), url(${ONBOARDING_HERO_IMAGE})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative p-5 md:p-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-card-hover px-3 py-1 text-xs font-medium text-foreground dark:bg-primary-hover/50 dark:text-primary-foreground">
              <BadgeCheck className="h-3.5 w-3.5" />
              Guided Onboarding
            </div>
            <h1 className="mt-3 text-2xl font-semibold text-foreground">Onboarding</h1>
            <p className="mt-1 text-muted">
              {data.template.name} ({data.template.year}) - {completion.completed}/{completion.total} erledigt
            </p>
            <p className="mt-1 text-xs text-muted">
              Geschaetzter Gesamtaufwand: {estimatedTotalMinutes} min | Offen: {remainingMinutes} min
            </p>
            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              <div className="rounded-lg border border-border bg-background p-3 dark:bg-card">
                <p className="text-xs text-muted">Erledigt</p>
                <p className="text-lg font-semibold text-foreground">{completion.completed}</p>
              </div>
              <div className="rounded-lg border border-border bg-background p-3 dark:bg-card">
                <p className="text-xs text-muted">Offen</p>
                <p className="text-lg font-semibold text-foreground">
                  {Math.max(completion.total - completion.completed, 0)}
                </p>
              </div>
              <div className="rounded-lg border border-border bg-background p-3 dark:bg-card">
                <p className="text-xs text-muted">Restzeit</p>
                <p className="text-lg font-semibold text-foreground">{remainingMinutes} min</p>
              </div>
            </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Checklist</CardTitle>
          <CardDescription>Arbeite die wichtigsten Punkte Schritt fuer Schritt mit direkten Shortcuts ab.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {data.tasks.map((task) => (
            <div
              key={task.id}
              className="flex items-start gap-3 rounded-lg border border-border p-3 text-sm"
            >
              <Checkbox
                checked={Boolean(task.progress?.completed_at)}
                onCheckedChange={() => toggleTask(task.id)}
              />
              <div className="mt-0.5 rounded-md bg-card-hover p-1.5 text-muted">
                {(() => {
                  const Icon = getTaskIcon(task.category);
                  return <Icon className="h-3.5 w-3.5" />;
                })()}
              </div>
              <span className="flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{task.title}</span>
                  {task.category && <Badge variant="secondary">{task.category}</Badge>}
                  {task.estimated_minutes ? (
                    <span className="inline-flex items-center gap-1 text-xs text-muted">
                      <Timer className="h-3 w-3" />
                      {task.estimated_minutes} min
                    </span>
                  ) : null}
                </span>
                {task.description && (
                  <span className="mt-1 block text-xs text-muted">{task.description}</span>
                )}
                {task.help_text && (
                  <span className="mt-1 block text-xs text-muted">{task.help_text}</span>
                )}
                {task.action_path && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2 h-7 px-2 text-xs"
                    onClick={() => openTaskAction(task.action_path)}
                  >
                    Jetzt oeffnen <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                )}
              </span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
